import { ArrowRight, Table2 } from "lucide-react";
import { SayzCta } from "@/components/widget-cta/SayzCta";
import { PrimaryAction } from "./PrimaryAction";

// ═══════════════════════════════════════════════════════════════════════════
// Footer sağ eylem — konteksle değişen ana buton
// ═══════════════════════════════════════════════════════════════════════════
export const FooterAction = ({
    currentId, canProceed, recommendation, welcomeRecommendation, hostStockStatus,
    previewRefusal, outOfRangeStrategy, welcomeMode, welcomeAnalysis, stepCopy, resultCopy, accent,
    widgetTemplate, widgetTokens, sizeTableButtonText,
    onNext, onWelcomeConfirm, onPartialContinue, onSelectSize, onOpenSizeChart,
}) => {
    if (currentId === "welcome") {
        if (welcomeMode === "empty") return null;
        // Partial · "Bir şey daha soracağım":
        //   • Eksik alan BODY field (göğüs/bel/kalça/omuz) ise WelcomeStep
        //     inline mini-soru render eder, seçince auto-advance eder → CTA
        //     footer'da yok (redundant).
        //   • Eksik alan BASIC field (fit/boy/kilo/yaş) ise inline soru
        //     YOKTUR (çünkü basic UI daha büyük, number input veya 3-opsiyonlu
        //     kart) → footer'da "Devam Et" CTA görünür, ilgili basic/fit
        //     step'e götürür. Bu olmazsa kullanıcı deadlock yaşıyordu
        //     (chip'ler + başlık var ama ilerletecek hiçbir UI yok).
        if (welcomeMode === "partial") {
            const missingBody = welcomeAnalysis?.missingBody || [];
            const missingBasic = welcomeAnalysis?.missingBasic || [];
            if (missingBody.length > 0) return null; // inline body step auto-advance eder
            if (missingBasic.length === 0) return null; // güvenlik ağı; buraya varsa complete demektir
            return (
                <PrimaryAction
                    onClick={onPartialContinue}
                    accent={accent}
                    testId="fms-welcome-partial-continue"
                    iconRight={ArrowRight}
                >
                    {stepCopy.welcomePartialContinueButton || "Devam Et"}
                </PrimaryAction>
            );
        }
        const isConfirm = welcomeMode === "confirm";
        const label = isConfirm
            ? (stepCopy.welcomeConfirmButton || "Evet, önerimi göster")
            : (resultCopy.selectSizeButton || stepCopy.welcomeContinueButton || "Bedeni Seç");
        // Fast-path (`recommend` mode) already shows the recommended size on
        // the welcome screen, so clicking "Bedeni Seç" there should auto-
        // select on the merchant site immediately — NOT navigate to a result
        // step the user has effectively already seen. `confirm` mode still
        // walks to the result screen so the visitor can review the number.
        const handleClick = isConfirm ? onWelcomeConfirm : (onSelectSize || onWelcomeConfirm);
        // On the welcome RECOMMEND fast-path we auto-select on the merchant
        // DOM — but only after the backend has returned a size. While the
        // measuring tape ritual is still running, keep the CTA visible but
        // disabled so the visitor sees where the action lives (no layout
        // shift when it enables) and can't fire a null-size auto-select.
        // Note: `recommendation` is reserved for the Result step (null here);
        // the welcome fast-path reads `welcomeRecommendation` instead.
        const welcomeCalculating = !isConfirm && !welcomeRecommendation;
        // Welcome Recommend's "swap CTA to size chart" cases — two
        // independent triggers, same visual outcome (SayzCta → Beden Tablosu):
        //   1. Backend refusal: outOfRange + kind_refusal strategy
        //      (visitor's profile sits outside the table's measurement
        //      space; see v29 WelcomeStep render path)
        //   2. Host DOM OOS: Site Entegrasyonu says this specific size is
        //      currently out of stock on the merchant site, so firing
        //      auto-select would either click a disabled button or do
        //      nothing visible to the visitor
        const welcomeRefusal = !isConfirm && welcomeRecommendation?.outOfRange && outOfRangeStrategy === "kind_refusal";
        const welcomeHostOOS = !isConfirm && hostStockStatus === "out-of-stock";
        if (welcomeRefusal || welcomeHostOOS) {
            return (
                <SayzCta
                    template={widgetTemplate}
                    tokens={widgetTokens}
                    icon={Table2}
                    label={sizeTableButtonText || "Beden Tablosu"}
                    testId="fms-welcome-open-size-chart"
                    onClick={onOpenSizeChart}
                />
            );
        }
        return (
            <PrimaryAction
                onClick={handleClick}
                disabled={welcomeCalculating}
                accent={accent}
                testId={isConfirm ? "fms-welcome-confirm-cta" : "fms-welcome-continue-cta"}
                iconRight={ArrowRight}
            >
                {label}
            </PrimaryAction>
        );
    }
    if (currentId === "result") {
        const outOfRange = recommendation?.outOfRange;
        const showRefusalUi = previewRefusal || (outOfRange && outOfRangeStrategy === "kind_refusal");
        const hostOOS = hostStockStatus === "out-of-stock";
        if (showRefusalUi || hostOOS) {
            // Nazik Ret CTA — iki tetikleyici de "size chart'a yönlendir"
            // sonucuna varıyor:
            //   • Backend outOfRange + kind_refusal → algoritmik ret
            //   • Host DOM OOS                      → stokta yok
            // Her iki durumda da auto-select güvenli değil, visitor'ı
            // güvenle beden tablosuna yönlendiriyoruz.
            return (
                <SayzCta
                    template={widgetTemplate}
                    tokens={widgetTokens}
                    icon={Table2}
                    label={sizeTableButtonText || "Beden Tablosu"}
                    testId="fms-preview-open-size-chart"
                    onClick={onOpenSizeChart}
                />
            );
        }
        // While the backend is computing the recommendation we show the
        // calling-card CTA but disable it — the visitor sees where the
        // action lives (no layout shift when it enables), yet can't fire
        // the auto-select with a null size payload.
        const isCalculating = !recommendation;
        return (
            <PrimaryAction
                onClick={onSelectSize}
                disabled={isCalculating}
                accent={accent}
                testId="fms-preview-select-size"
                iconRight={ArrowRight}
            >
                {resultCopy.selectSizeButton || "Bedeni Seç"}
            </PrimaryAction>
        );
    }
    // Basic Info: the only mid-flow screen with a manual "Devam" — fit + body
    // screens auto-advance the moment the visitor selects an option, so a CTA
    // there would be redundant (and briefly click-baity while disabled).
    if (currentId === "basic") {
        return (
            <PrimaryAction
                onClick={onNext}
                disabled={!canProceed}
                accent={accent}
                testId="fms-preview-next"
                iconRight={ArrowRight}
            >
                Devam
            </PrimaryAction>
        );
    }
    // Fit / body screens auto-advance on selection — render nothing on the right.
    return null;
};
